import { useApolloClient, useMutation } from '@apollo/client'
import { useCallback } from 'react'
import { MoveQuestionnaireEntryV2MutationVariables } from '../../data/gql-gen/questionnaire/graphql'
import DRAFT_QUESTIONNAIRE_ENTRY from '../../data/gql/questionnaire/fragments/draftQuestionnaireEntry'
import { draftQuestionnaireRefetchQuery } from '../../data/gql/questionnaire/queries'
import { LoggerErrorType } from '../../data/logger'
import { DraftQuestionnaireEntry } from '../../data/model/questionnaire'
import { SECTION_ITEM_LOOPING_CONFIG } from '../../hooks/questionnaire/loopingConfig.questionnaire.gql'
import { useOptionalWaveId } from '../../hooks/useOptionalWaveId'
import { useProjectId } from '../../hooks/useProjectId'
import {
  questionNewlyCreatedNumber,
  responseOptionLkNewlyAdded
} from '../../hooks/useResetNewlyCreatedEntry'
import { useSurveyId } from '../../hooks/useSurveyId'
import { captureApolloError } from '../../utils/HelperFunctions'
import { newEntryId } from '../../utils/questionnaireUtils'
import { useHasEarlierWaves } from './Kpi/HasEarlierWavesContext'
import { useConfirmLoopedSectionMove } from './Kpi/LoopedSectionMoveConfirm'
import {
  LOOPED_SECTION_KPI_REASON,
  describeEntriesKpiImpact,
  getEntriesKpiImpact
} from './Kpi/kpiImpact'
import { MOVE_QUESTIONNAIRE_ENTRY } from './Sidebar/SidebarQuestionnaireItemList/SidebarQuestionnaireItemList.mutations'

interface DragAndDropItem {
  path: string
  position: number
}

export type Source = DragAndDropItem
export type Destination = DragAndDropItem

export const useDragAndDrop = () => {
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()
  const client = useApolloClient()
  const confirmLoopedSectionMove = useConfirmLoopedSectionMove()
  const hasEarlierWaves = useHasEarlierWaves()

  const [moveQuestionnaireEntry] = useMutation(MOVE_QUESTIONNAIRE_ENTRY, {
    context: { clientName: 'questionnaire' },
    refetchQueries: [
      draftQuestionnaireRefetchQuery(projectId, surveyId, waveId)
    ],
    onError: (error) => {
      captureApolloError(
        LoggerErrorType.ApolloMutation,
        'moveQuestionniareEntry',
        error
      )
    }
  })

  // Read when dropped rather than subscribed to: every drop target calls this hook.
  const getLoopedSectionMoveKpiImpact = useCallback(
    (entryId: string, sectionId: string) => {
      const section = client.readFragment({
        id: client.cache.identify({
          __typename: 'DraftSectionItem',
          sectionId
        }),
        fragment: SECTION_ITEM_LOOPING_CONFIG,
        fragmentName: 'SectionItemLoopingConfig'
      })
      if (!section) {
        return null
      }
      if (!section.isLoopingEnabled) {
        return []
      }
      const entry = client.readFragment<DraftQuestionnaireEntry>({
        id: client.cache.identify({
          __typename: 'DraftQuestionnaireEntry',
          id: entryId
        }),
        fragment: DRAFT_QUESTIONNAIRE_ENTRY,
        fragmentName: 'DraftQuestionnaireEntry'
      })
      return entry ? getEntriesKpiImpact([entry]) : null
    },
    [client]
  )

  /** `onMove` runs only if the move goes ahead, which can wait for a confirm. */
  const moveCard = useCallback(
    (
      entryId: string,
      source: Source,
      dest: Destination,
      onMove?: () => void
    ) => {
      if (source.path === dest.path || !entryId) return

      const contextFromPath = (path: string) =>
        path.includes('/') ? 'section' : 'sidebar'
      const sourceContext = contextFromPath(source.path)
      const destContext = contextFromPath(dest.path)

      const variables: MoveQuestionnaireEntryV2MutationVariables = {
        input: {
          projectId,
          surveyId,
          waveId,
          entryId,
          position: dest.position
        }
      }

      const setSectionId = (sectionId?: string | null) => {
        variables.input.sectionId = { value: sectionId }
      }

      const getSectionId = (dragAndDropItem: DragAndDropItem) =>
        dragAndDropItem.path.split('/')[0]

      if (sourceContext === 'sidebar' && destContext === 'section') {
        // move from sidebar to section
        const destSectionId = getSectionId(dest)
        setSectionId(destSectionId)
      } else if (sourceContext === 'section' && destContext === 'sidebar') {
        // move from section to sidebar
        setSectionId(null)
      } else if (sourceContext === 'section' && destContext === 'section') {
        // move within section or to another section
        const sourceSectionId = getSectionId(source)
        const destSectionId = getSectionId(dest)
        // if moving into another section, update the sectionId
        if (sourceSectionId !== destSectionId) {
          setSectionId(destSectionId)
        }
      }

      const move = () => {
        onMove?.()
        moveQuestionnaireEntry({ variables })

        questionNewlyCreatedNumber(undefined)
        newEntryId(undefined)
        responseOptionLkNewlyAdded(undefined)
      }

      const destSectionId = variables.input.sectionId?.value
      const kpiImpact = destSectionId
        ? getLoopedSectionMoveKpiImpact(entryId, destSectionId)
        : []
      if (!kpiImpact) {
        return
      }
      if (confirmLoopedSectionMove && kpiImpact.length > 0) {
        confirmLoopedSectionMove({
          message: `${LOOPED_SECTION_KPI_REASON} ${describeEntriesKpiImpact(
            kpiImpact,
            hasEarlierWaves
          )}`,
          move
        })
        return
      }
      move()
    },
    [
      confirmLoopedSectionMove,
      getLoopedSectionMoveKpiImpact,
      hasEarlierWaves,
      moveQuestionnaireEntry,
      projectId,
      surveyId,
      waveId
    ]
  )

  return moveCard
}
